/**
 * A* Pathfinding Algorithm
 * Simple grid-based pathfinding for enemy AI
 */
export class AStarPathfinding {
    constructor(gridWidth, gridHeight, cellSize = 1) {
        this.gridWidth = gridWidth;
        this.gridHeight = gridHeight;
        this.cellSize = cellSize;
        this.grid = this.createGrid();
    }
    
    /**
     * Create empty grid
     */
    createGrid() {
        const grid = [];
        for (let x = 0; x < this.gridWidth; x++) {
            grid[x] = [];
            for (let y = 0; y < this.gridHeight; y++) {
                grid[x][y] = {
                    x,
                    y,
                    walkable: true,
                    f: 0,
                    g: 0,
                    h: 0,
                    parent: null
                };
            }
        }
        return grid;
    }
    
    /**
     * Set obstacle at grid position
     */
    setObstacle(x, y, walkable = false) {
        if (this.isValidPosition(x, y)) {
            this.grid[x][y].walkable = walkable;
        }
    }
    
    /**
     * Check if position is valid
     */
    isValidPosition(x, y) {
        return x >= 0 && x < this.gridWidth && y >= 0 && y < this.gridHeight;
    }
    
    /**
     * Convert world position to grid coordinates
     */
    worldToGrid(worldX, worldZ) {
        const gridX = Math.floor((worldX + this.gridWidth * this.cellSize / 2) / this.cellSize);
        const gridY = Math.floor((worldZ + this.gridHeight * this.cellSize / 2) / this.cellSize);
        return { x: gridX, y: gridY };
    }
    
    /**
     * Convert grid coordinates to world position
     */
    gridToWorld(gridX, gridY) {
        const worldX = (gridX - this.gridWidth / 2) * this.cellSize;
        const worldZ = (gridY - this.gridHeight / 2) * this.cellSize;
        return { x: worldX, z: worldZ };
    }
    
    /**
     * Heuristic function (Manhattan distance)
     */
    heuristic(nodeA, nodeB) {
        return Math.abs(nodeA.x - nodeB.x) + Math.abs(nodeA.y - nodeB.y);
    }
    
    /**
     * Get neighbors of a node
     */
    getNeighbors(node) {
        const neighbors = [];
        const directions = [
            { x: 0, y: -1 }, // North
            { x: 1, y: 0 },  // East
            { x: 0, y: 1 },  // South
            { x: -1, y: 0 }, // West
            { x: 1, y: -1 }, // Northeast
            { x: 1, y: 1 },  // Southeast
            { x: -1, y: 1 }, // Southwest
            { x: -1, y: -1 } // Northwest
        ];
        
        for (const dir of directions) {
            const x = node.x + dir.x;
            const y = node.y + dir.y;
            
            if (this.isValidPosition(x, y) && this.grid[x][y].walkable) {
                neighbors.push(this.grid[x][y]);
            }
        }
        
        return neighbors;
    }
    
    /**
     * Find path using A* algorithm
     * @param {Object} start - Start position {x, z}
     * @param {Object} end - End position {x, z}
     * @returns {Array} Array of world positions or empty array if no path found
     */
    findPath(start, end) {
        const startGrid = this.worldToGrid(start.x, start.z);
        const endGrid = this.worldToGrid(end.x, end.z);
        
        if (!this.isValidPosition(startGrid.x, startGrid.y) || 
            !this.isValidPosition(endGrid.x, endGrid.y)) {
            return [];
        }
        
        const startNode = this.grid[startGrid.x][startGrid.y];
        const endNode = this.grid[endGrid.x][endGrid.y];
        
        if (!startNode.walkable || !endNode.walkable) {
            return [];
        }
        
        const openList = [];
        const closedList = [];
        
        // Reset grid
        for (let x = 0; x < this.gridWidth; x++) {
            for (let y = 0; y < this.gridHeight; y++) {
                this.grid[x][y].f = 0;
                this.grid[x][y].g = 0;
                this.grid[x][y].h = 0;
                this.grid[x][y].parent = null;
            }
        }
        
        openList.push(startNode);
        
        while (openList.length > 0) {
            // Find node with lowest f score
            let lowestIndex = 0;
            for (let i = 1; i < openList.length; i++) {
                if (openList[i].f < openList[lowestIndex].f) {
                    lowestIndex = i;
                }
            }
            
            const currentNode = openList[lowestIndex];
            
            // Remove from open list and add to closed list
            openList.splice(lowestIndex, 1);
            closedList.push(currentNode);
            
            // Found the goal
            if (currentNode === endNode) {
                const path = [];
                let current = currentNode;
                
                while (current) {
                    const worldPos = this.gridToWorld(current.x, current.y);
                    path.unshift({ x: worldPos.x, y: 0, z: worldPos.z });
                    current = current.parent;
                }
                
                return path;
            }
            
            // Check neighbors
            const neighbors = this.getNeighbors(currentNode);
            for (const neighbor of neighbors) {
                if (closedList.includes(neighbor)) {
                    continue;
                }
                
                const gScore = currentNode.g + 1;
                const hScore = this.heuristic(neighbor, endNode);
                const fScore = gScore + hScore;
                
                if (!openList.includes(neighbor)) {
                    openList.push(neighbor);
                } else if (gScore >= neighbor.g) {
                    continue;
                }
                
                neighbor.parent = currentNode;
                neighbor.g = gScore;
                neighbor.h = hScore;
                neighbor.f = fScore;
            }
        }
        
        // No path found
        return [];
    }
    
    /**
     * Update obstacles from Three.js scene
     * @param {THREE.Scene} scene - Three.js scene
     */
    updateObstaclesFromScene(scene) {
        // Reset grid
        for (let x = 0; x < this.gridWidth; x++) {
            for (let y = 0; y < this.gridHeight; y++) {
                this.grid[x][y].walkable = true;
            }
        }
        
        // Check for obstacles in scene
        scene.traverse((object) => {
            if (object.isMesh && object.userData.isObstacle) {
                const gridPos = this.worldToGrid(object.position.x, object.position.z);
                if (this.isValidPosition(gridPos.x, gridPos.y)) {
                    this.setObstacle(gridPos.x, gridPos.y, false);
                    
                    // Also mark surrounding cells as obstacles
                    for (let dx = -1; dx <= 1; dx++) {
                        for (let dy = -1; dy <= 1; dy++) {
                            const nx = gridPos.x + dx;
                            const ny = gridPos.y + dy;
                            if (this.isValidPosition(nx, ny)) {
                                this.setObstacle(nx, ny, false);
                            }
                        }
                    }
                }
            }
        });
    }
}


